import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Activity, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  RefreshCw, Brain, Loader2, DollarSign, Clock, Target, ArrowRight,
  AlertCircle, ChevronDown, ChevronUp, Zap, Shield, X, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Losing Position Card with Rolling Recommendation
const LosingPositionCard = ({ position, onGetRollDetails }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className="bg-red-500/10 border-red-500/30 hover:border-red-500/50 transition-colors">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">
                {position.symbol} ${position.strike_price} {position.option_type.toUpperCase()}
              </h3>
              <p className="text-sm text-slate-400">
                Exp: {position.expiration_date} ({position.days_to_expiration} days) • {position.position_type}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-red-400">
              {position.pnl_percent.toFixed(1)}%
            </div>
            <div className="text-sm text-red-300">
              ${position.pnl_dollar.toFixed(2)}
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Price Info */}
        <div className="flex gap-4 text-sm">
          <div>
            <span className="text-slate-500">Entry:</span>
            <span className="text-white ml-1">${position.entry_premium.toFixed(2)}</span>
          </div>
          <div>
            <span className="text-slate-500">Current:</span>
            <span className="text-white ml-1">${position.current_premium.toFixed(2)}</span>
          </div>
          <div>
            <span className="text-slate-500">Qty:</span>
            <span className="text-white ml-1">{Math.abs(position.quantity)}</span>
          </div>
        </div>
        
        {/* Urgency Badge */}
        <div className="flex items-center gap-2">
          <Badge className={`${
            position.urgency === 'HIGH' ? 'bg-red-600' : 
            position.urgency === 'MEDIUM' ? 'bg-amber-600' : 'bg-slate-600'
          }`}>
            {position.urgency === 'HIGH' ? <AlertCircle className="w-3 h-3 mr-1" /> : null}
            {position.urgency} URGENCY
          </Badge>
          <Badge variant="outline" className="border-red-500/50 text-red-400">
            {position.action_needed}
          </Badge>
        </div>
        
        {/* AI Recommendation Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          className="w-full text-slate-400 hover:text-white border border-slate-700"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4 mr-2" />
              Hide Rolling Strategy
            </>
          ) : (
            <>
              <Brain className="w-4 h-4 mr-2" />
              View Rolling Strategy
            </>
          )}
        </Button>
        
        {expanded && position.ai_recommendation && (
          <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
            <div className="prose prose-invert prose-sm max-w-none">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-lg font-bold text-white mt-4 mb-2" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-base font-semibold text-indigo-400 mt-3 mb-1" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-sm font-medium text-emerald-400 mt-2 mb-1" {...props} />,
                  p: ({node, ...props}) => <p className="text-slate-300 my-1 text-sm" {...props} />,
                  strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                  li: ({node, ...props}) => <li className="text-slate-300 text-sm" {...props} />,
                }}
              >
                {position.ai_recommendation}
              </ReactMarkdown>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={() => onGetRollDetails(position)}
            className="flex-1 bg-red-600 hover:bg-red-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Get Specific Roll
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Winning Position Card with Profit Taking Recommendation
const WinningPositionCard = ({ position }) => {
  return (
    <Card className="bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/50 transition-colors">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">
                {position.symbol} ${position.strike_price} {position.option_type.toUpperCase()}
              </h3>
              <p className="text-sm text-slate-400">
                Exp: {position.expiration_date} ({position.days_to_expiration} days) • {position.position_type}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-emerald-400">
              +{position.pnl_percent.toFixed(1)}%
            </div>
            <div className="text-sm text-emerald-300">
              +${position.pnl_dollar.toFixed(2)}
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Price Info */}
        <div className="flex gap-4 text-sm">
          <div>
            <span className="text-slate-500">Entry:</span>
            <span className="text-white ml-1">${position.entry_premium.toFixed(2)}</span>
          </div>
          <div>
            <span className="text-slate-500">Current:</span>
            <span className="text-white ml-1">${position.current_premium.toFixed(2)}</span>
          </div>
          <div>
            <span className="text-slate-500">Qty:</span>
            <span className="text-white ml-1">{Math.abs(position.quantity)}</span>
          </div>
        </div>
        
        {/* Take Profit Recommendation */}
        <div className="p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-emerald-400">TAKE PROFITS</div>
              <div className="text-sm text-slate-300">{position.recommendation}</div>
            </div>
          </div>
        </div>
        
        <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle className="w-4 h-4 mr-2" />
          Close Position to Lock Gains
        </Button>
      </CardContent>
    </Card>
  );
};

// Main Portfolio Health Analyzer Component
const PortfolioHealthAnalyzer = ({ positions = [], onClose }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [aiModel, setAiModel] = useState('gemini');
  const [lossThreshold, setLossThreshold] = useState(25);
  const [gainThreshold, setGainThreshold] = useState(50);
  const [rollDetailsModal, setRollDetailsModal] = useState(null);
  const [loadingRollDetails, setLoadingRollDetails] = useState(false);
  
  const analyzePortfolio = async () => {
    if (!positions || positions.length === 0) {
      toast.error('No positions to analyze');
      return;
    }
    
    setAnalyzing(true);
    toast.info('Analyzing portfolio health... This may take 30-60 seconds');
    
    try {
      // Format positions for API
      const formattedPositions = positions.map(pos => ({
        id: pos.id,
        underlying_symbol: pos.underlying_symbol || pos.symbol,
        option_type: pos.option_type || 'call',
        strike_price: pos.strike_price || 0,
        expiration_date: pos.expiration_date || '',
        quantity: pos.quantity || 1,
        entry_premium: pos.entry_premium || 0,
        current_premium: pos.current_premium || pos.entry_premium || 0,
        platform: pos.platform
      }));
      
      const response = await axios.post(`${API}/portfolio-health/analyze`, {
        positions: formattedPositions,
        loss_threshold: lossThreshold,
        gain_threshold: gainThreshold,
        ai_model: aiModel
      }, { timeout: 120000 });
      
      if (response.data.success) {
        setReport(response.data);
        toast.success('Portfolio analysis complete!');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze portfolio');
    } finally {
      setAnalyzing(false);
    }
  };
  
  const getSpecificRoll = async (position) => {
    setLoadingRollDetails(true);
    try {
      const response = await axios.post(`${API}/portfolio-health/suggest-roll`, null, {
        params: {
          symbol: position.symbol,
          option_type: position.option_type,
          current_strike: position.strike_price,
          expiration_date: position.expiration_date,
          entry_premium: position.entry_premium,
          current_premium: position.current_premium,
          quantity: position.quantity,
          ai_model: aiModel
        },
        timeout: 60000
      });
      
      setRollDetailsModal(response.data);
    } catch (error) {
      toast.error('Failed to get roll details');
    } finally {
      setLoadingRollDetails(false);
    }
  };
  
  const getHealthColor = (score) => {
    switch (score) {
      case 'HEALTHY': return 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30';
      case 'NEEDS_ATTENTION': return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
      case 'CRITICAL': return 'text-red-400 bg-red-500/20 border-red-500/30';
      default: return 'text-slate-400 bg-slate-500/20 border-slate-500/30';
    }
  };
  
  const getHealthIcon = (score) => {
    switch (score) {
      case 'HEALTHY': return <CheckCircle className="w-6 h-6" />;
      case 'NEEDS_ATTENTION': return <AlertTriangle className="w-6 h-6" />;
      case 'CRITICAL': return <AlertCircle className="w-6 h-6" />;
      default: return <Activity className="w-6 h-6" />;
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-6 h-6 text-indigo-400" />
          <div>
            <h2 className="text-xl font-bold text-white">Portfolio Health Analyzer</h2>
            <p className="text-sm text-slate-400">
              Identify losing positions for rolling and winners for profit taking
            </p>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      {/* Controls */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-slate-400 text-sm">Loss Threshold</Label>
              <Select value={String(lossThreshold)} onValueChange={v => setLossThreshold(Number(v))}>
                <SelectTrigger className="w-32 bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="15" className="text-white">15%</SelectItem>
                  <SelectItem value="20" className="text-white">20%</SelectItem>
                  <SelectItem value="25" className="text-white">25%</SelectItem>
                  <SelectItem value="30" className="text-white">30%</SelectItem>
                  <SelectItem value="40" className="text-white">40%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-slate-400 text-sm">Gain Threshold</Label>
              <Select value={String(gainThreshold)} onValueChange={v => setGainThreshold(Number(v))}>
                <SelectTrigger className="w-32 bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="25" className="text-white">25%</SelectItem>
                  <SelectItem value="50" className="text-white">50%</SelectItem>
                  <SelectItem value="75" className="text-white">75%</SelectItem>
                  <SelectItem value="100" className="text-white">100%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-slate-400 text-sm">AI Model</Label>
              <Select value={aiModel} onValueChange={setAiModel}>
                <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="gemini" className="text-white">Gemini Pro</SelectItem>
                  <SelectItem value="claude" className="text-white">Claude Sonnet</SelectItem>
                  <SelectItem value="fast" className="text-white">Gemini Flash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={analyzePortfolio}
              disabled={analyzing || positions.length === 0}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Analyze Portfolio ({positions.length} positions)
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Results */}
      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Health Score */}
            <Card className={`border ${getHealthColor(report.portfolio_health_score)}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={getHealthColor(report.portfolio_health_score).split(' ')[0]}>
                  {getHealthIcon(report.portfolio_health_score)}
                </div>
                <div>
                  <div className="text-sm text-slate-400">Health Score</div>
                  <div className={`text-lg font-bold ${getHealthColor(report.portfolio_health_score).split(' ')[0]}`}>
                    {report.portfolio_health_score.replace('_', ' ')}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Total P/L */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="text-sm text-slate-400">Total P/L</div>
                <div className={`text-xl font-bold ${report.total_pnl_dollar >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {report.total_pnl_dollar >= 0 ? '+' : ''}${report.total_pnl_dollar.toFixed(2)}
                </div>
              </CardContent>
            </Card>
            
            {/* Losing */}
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-4">
                <div className="text-sm text-red-300">Losing (&gt;{lossThreshold}%)</div>
                <div className="text-xl font-bold text-red-400">
                  {report.losing_positions_count} positions
                </div>
              </CardContent>
            </Card>
            
            {/* Winning */}
            <Card className="bg-emerald-500/10 border-emerald-500/30">
              <CardContent className="p-4">
                <div className="text-sm text-emerald-300">Winning (&gt;{gainThreshold}%)</div>
                <div className="text-xl font-bold text-emerald-400">
                  {report.winning_positions_count} positions
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* AI Summary */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-white">
                <Brain className="w-5 h-5 text-purple-400" />
                AI Portfolio Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-invert prose-sm max-w-none">
                <ReactMarkdown
                  components={{
                    p: ({node, ...props}) => <p className="text-slate-300 my-2" {...props} />,
                    strong: ({node, ...props}) => <strong className="text-white" {...props} />,
                  }}
                >
                  {report.ai_summary}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
          
          {/* Losing Positions Section */}
          {report.losing_positions.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                <h3 className="text-lg font-semibold text-white">
                  Positions Needing Attention ({report.losing_positions.length})
                </h3>
                <Badge className="bg-red-600">ROLL OR CLOSE</Badge>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {report.losing_positions.map((pos, idx) => (
                  <LosingPositionCard 
                    key={idx} 
                    position={pos} 
                    onGetRollDetails={getSpecificRoll}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Winning Positions Section */}
          {report.winning_positions.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-semibold text-white">
                  Positions to Take Profits ({report.winning_positions.length})
                </h3>
                <Badge className="bg-emerald-600">TAKE PROFITS</Badge>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {report.winning_positions.map((pos, idx) => (
                  <WinningPositionCard key={idx} position={pos} />
                ))}
              </div>
            </div>
          )}
          
          {/* No Issues */}
          {report.losing_positions.length === 0 && report.winning_positions.length === 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Portfolio Looking Good!</h3>
                <p className="text-slate-400">
                  No positions are losing more than {lossThreshold}% or gaining more than {gainThreshold}%.
                  All {report.neutral_positions_count} positions are within normal range.
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Roll Details Modal */}
      <Dialog open={!!rollDetailsModal} onOpenChange={() => setRollDetailsModal(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <RefreshCw className="w-5 h-5 text-indigo-400" />
              Specific Rolling Strategy
            </DialogTitle>
          </DialogHeader>
          
          {loadingRollDetails ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 mx-auto text-indigo-400 animate-spin mb-4" />
              <p className="text-slate-400">Getting specific roll recommendation...</p>
            </div>
          ) : rollDetailsModal ? (
            <div className="space-y-4">
              {/* Current Position */}
              <div className="p-4 bg-slate-800 rounded-lg">
                <h4 className="text-sm font-medium text-slate-400 mb-2">Current Position</h4>
                <div className="flex flex-wrap gap-3 text-sm">
                  <Badge variant="outline" className="border-slate-600 text-white">
                    {rollDetailsModal.symbol}
                  </Badge>
                  <Badge variant="outline" className="border-slate-600 text-white">
                    ${rollDetailsModal.current_position.strike} {rollDetailsModal.current_position.option_type.toUpperCase()}
                  </Badge>
                  <Badge variant="outline" className="border-slate-600 text-white">
                    Exp: {rollDetailsModal.current_position.expiration}
                  </Badge>
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                    P/L: {rollDetailsModal.current_position.pnl_percent.toFixed(1)}%
                  </Badge>
                </div>
                <div className="mt-2 text-sm text-slate-400">
                  Stock Price: ${rollDetailsModal.current_stock_price}
                </div>
              </div>
              
              {/* AI Recommendation */}
              <div className="prose prose-invert prose-sm max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-lg font-bold text-white mt-4 mb-2" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-base font-semibold text-indigo-400 mt-3 mb-1" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-sm font-medium text-emerald-400 mt-2 mb-1" {...props} />,
                    p: ({node, ...props}) => <p className="text-slate-300 my-1 text-sm" {...props} />,
                    strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                    li: ({node, ...props}) => <li className="text-slate-300 text-sm" {...props} />,
                  }}
                >
                  {rollDetailsModal.recommendation}
                </ReactMarkdown>
              </div>
              
              {/* Available Expirations */}
              {rollDetailsModal.available_expirations && (
                <div className="p-3 bg-slate-800 rounded-lg">
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Available Expirations</h4>
                  <div className="flex flex-wrap gap-2">
                    {rollDetailsModal.available_expirations.map((exp, idx) => (
                      <Badge key={idx} variant="outline" className="border-slate-600 text-slate-300">
                        {exp}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioHealthAnalyzer;
